package io.horion.cv.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaMinus
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.components.Footer
import io.horion.cv.components.Topbar
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.UBUNTU
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

data class QuestionsTag(
    val question: String,
    val response: String
)

val faq = listOf(
    QuestionsTag("Combien coûte un CV ?",
        "500 XAF par document. C'est un paiement unique, sans abonnement ni frais cachés : vous ne payez que les CV que vous décidez de télécharger."),
    QuestionsTag("Quels moyens de paiement acceptez-vous ?",
        "Orange Money et MTN Mobile Money. L'opérateur est détecté automatiquement à partir de votre numéro."),
    QuestionsTag("J'ai payé, quand puis-je télécharger mon CV ?",
        "Le téléchargement est immédiat, dès la confirmation du paiement."),
    QuestionsTag("Puis-je modifier mon CV après l'avoir commencé ?",
        "Oui. Vous pouvez modifier chaque section autant de fois que vous le souhaitez avant de télécharger, et l'aperçu se met à jour en temps réel."),
    QuestionsTag("Dois-je créer un compte pour utiliser Horion ?",
        "Vous pouvez composer votre CV librement ; un compte n'est demandé qu'au moment du téléchargement."),
    QuestionsTag("Puis-je choisir parmi plusieurs modèles ?",
        "Oui, plusieurs modèles sont disponibles. Vous passez de l'un à l'autre à tout moment sans ressaisir vos informations."),
    QuestionsTag("Mon CV sera-t-il au bon format pour l'envoyer à un employeur ?",
        "Votre CV est généré en PDF, prêt à être envoyé ou imprimé."),
    QuestionsTag("Puis-je utiliser Horion sur mon téléphone ?",
        "Oui, l'interface s'adapte au mobile comme à l'ordinateur."),
    QuestionsTag("L'application est-elle disponible en mode sombre ?",
        "Oui, un thème clair et un thème sombre sont proposés."),
    QuestionsTag("Puis-je créer une lettre de motivation sur Horion ?",
        "Oui, en plus des CV, Horion vous permet de rédiger une lettre de motivation."),
    QuestionsTag("Puis-je adapter ma lettre à une offre précise ?",
        "Oui, vous personnalisez le contenu de votre lettre selon le poste et l'enterprise visés."),
    QuestionsTag("Dans quel format ma lettre est-elle générée ?",
        "Votre lettre est générée au format PDF."),
    QuestionsTag("Puis-je modifier ma lettre avant de la télécharger ?",
        "Oui, vous pouvez la modifier autant de fois que nécessaire, avec un aperçu en temps réel.")
)

@Page
@Composable
fun Faq() {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .backgroundColor(if (isDark) Colors.DarkBG else Colors.White)
            .styleModifier { property("transition", "background-color 300ms ease") },
        verticalArrangement = Arrangement.SpaceBetween
    ) {
        Topbar()
        FaqContent()
        Footer()
    }
}

@Composable
fun FaqContent() {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    var openedSection by remember { mutableStateOf<String?>(null) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 40.px, bottom = 80.px, left = 16.px, right = 16.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            text = "Questions fréquentes",
            modifier = Modifier
                .fontSize(30.px)
                .color(if (isDark) Colors.White else Colors.DarkBlue)
                .fontFamily(UBUNTU)
                .fontWeight(FontWeight.Bold)
                .margin(bottom = 32.px)
                .textAlign(TextAlign.Center)
                .whiteSpace(WhiteSpace.PreWrap)
        )

        Column(
            modifier = Modifier
                .maxWidth(714.px)
                .fillMaxWidth()
        ) {
            faq.forEach { item ->
                val isOpen = item.question == openedSection
                SpanCompose(
                    text = item.question,
                    description = item.response,
                    isOpen = isOpen,
                    onClick = { openedSection = if (isOpen) null else item.question }
                )
            }
        }
    }
}

@Composable
fun SpanCompose(
    text: String,
    description: String,
    isOpen: Boolean,
    onClick: () -> Unit
) {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    // ── card ─────────────────────────────────────────────────────────────
    // light : fond LightGray  / bordure LightGray  (identique à avant)
    // dark  : fond holder     / bordure borders
    val cardBg     = if (isDark) Colors.holder   else Colors.LightGray
    val cardBorder = if (isDark) Colors.borders  else Colors.LightGray

    // ── textes ───────────────────────────────────────────────────────────
    // light : question en Black, réponse en Graytext  (identique à avant)
    // dark  : question en White, réponse en DarkGray
    val questionColor = if (isDark) Colors.White    else Colors.Black
    val answerColor   = if (isDark) Colors.DarkGray else Colors.Graytext

    // ── icônes +/− : SVG gris → on les inverse en dark pour qu'elles soient blanches
    val iconFilter = if (isDark) "invert(1)" else "none"

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 12.px)
            .backgroundColor(cardBg)
            .borderRadius(12.px)
            .border(1.px, LineStyle.Solid, cardBorder)
            .padding(14.px)
            .cursor(Cursor.Pointer)
            .styleModifier { property("transition", "background-color 300ms ease") }
            .onClick { onClick() }
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {


            if (isOpen) {
                FaMinus(
                    modifier = Modifier
                        .size(24.px)
                        .margin(right = 8.px)
                        .styleModifier { property("filter", iconFilter) }
                )
            } else {
                FaPlus(
                    modifier = Modifier
                        .size(24.px)
                        .margin(right = 8.px)
                        .styleModifier { property("filter", iconFilter) }
                )
            }



            SpanText(
                text = text,
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(16.px)
                    .fontFamily(UBUNTU)
                    .fontWeight(FontWeight.Medium)
                    .color(questionColor)
            )
        }

        if (isOpen) {
            SpanText(
                text = description,
                modifier = Modifier
                    .margin(top = 12.px, left = 32.px)
                    .fontSize(16.px)
                    .fontFamily(UBUNTU)
                    .color(answerColor)
                    .whiteSpace(WhiteSpace.PreWrap)
            )
        }
    }
}